import { useState } from "react";
import type { TutorPresentation } from "./tutorProfileApi.js";
import { Dialog } from "./ui/Dialog.js";
import "./tutorProfiles.css";

export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ""}${parts.at(-1)?.[0] ?? ""}`.toUpperCase();
}

export function TutorAvatar({
  name,
  pictureUrl,
  compact = false,
}: {
  name: string;
  pictureUrl: string | null;
  compact?: boolean;
}) {
  return (
    <span className={`tutor-avatar${compact ? " tutor-avatar-compact" : ""}`} aria-hidden="true">
      <span className="tutor-avatar-initials">{initialsFor(name)}</span>
      {pictureUrl && (
        <img
          src={pictureUrl}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={(event) => event.currentTarget.remove()}
        />
      )}
    </span>
  );
}

/** Split a plain-text biography into paragraphs, dropping blank ones. */
export function biographyParagraphs(biography: string | null): string[] {
  if (!biography) return [];
  return biography
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

/**
 * The first paragraph is the introduction; the rest stays behind a toggle so a
 * long biography does not crowd the lesson overview.
 */
export function TutorBiography({ name, biography }: { name: string; biography: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const paragraphs = biographyParagraphs(biography);
  if (paragraphs.length === 0) return null;
  const [lead, ...rest] = paragraphs;

  return (
    <div className="tutor-card-biography">
      <p>{lead}</p>
      {expanded && rest.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
      {rest.length > 0 && (
        <button
          type="button"
          className="link tutor-card-biography-toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? "Show less" : `Read more about ${name}`}
        </button>
      )}
    </div>
  );
}

export function tutorContext(tutor: TutorPresentation): string | null {
  if (tutor.instruments.length > 0) return tutor.instruments.join(" · ");
  if (tutor.services.length > 0) return tutor.services.join(" · ");
  return null;
}

/** The whole biography, for the "Learn more" dialog. */
function TutorBiographyDialog({
  tutor,
  onClose,
}: {
  tutor: TutorPresentation;
  onClose: () => void;
}) {
  const paragraphs = biographyParagraphs(tutor.biography);
  return (
    <Dialog title={tutor.displayName} description={tutorContext(tutor)} onClose={onClose}>
      <div className="tutor-dialog-body">
        <TutorAvatar name={tutor.displayName} pictureUrl={tutor.pictureUrl} />
        <div className="tutor-card-biography">
          {paragraphs.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

/**
 * One tutor as a compact chip: picture, name and what they teach, with the
 * biography kept behind "Learn more" so the top of Home stays small.
 */
export function TutorChip({ tutor }: { tutor: TutorPresentation }) {
  const [open, setOpen] = useState(false);
  const context = tutorContext(tutor);
  const hasBiography = biographyParagraphs(tutor.biography).length > 0;

  return (
    <div className="tutor-chip">
      <TutorAvatar name={tutor.displayName} pictureUrl={tutor.pictureUrl} />
      <div className="tutor-chip-copy">
        <strong className="tutor-chip-name">{tutor.displayName}</strong>
        {context && <div className="small muted tutor-chip-context">{context}</div>}
        {hasBiography && (
          <button
            type="button"
            className="link small tutor-chip-more"
            aria-haspopup="dialog"
            onClick={() => setOpen(true)}
          >
            Learn more
          </button>
        )}
      </div>
      {open && <TutorBiographyDialog tutor={tutor} onClose={() => setOpen(false)} />}
    </div>
  );
}

export function TutorChips({ tutors = [] }: { tutors?: readonly TutorPresentation[] }) {
  if (tutors.length === 0) return null;
  return (
    <div className="tutor-chip-row">
      {tutors.map((tutor) => (
        <TutorChip tutor={tutor} key={tutor.tutorProfileId} />
      ))}
    </div>
  );
}

/** Kept for callers that still render tutors as full cards. */
export function TutorCards({ tutors = [] }: { tutors?: readonly TutorPresentation[] }) {
  if (tutors.length === 0) return null;
  const heading = tutors.length === 1 ? "Your tutor" : "Your tutors";

  return (
    <section className="student-home-tutors" aria-labelledby="student-home-tutors-heading">
      <h2 id="student-home-tutors-heading">{heading}</h2>
      <div className="tutor-card-grid">
        {tutors.map((tutor) => {
          const context = tutorContext(tutor);
          return (
            <article className="panel tutor-card" key={tutor.tutorProfileId}>
              <TutorAvatar name={tutor.displayName} pictureUrl={tutor.pictureUrl} />
              <div className="tutor-card-copy">
                <h3>{tutor.displayName}</h3>
                {context && <div className="small muted tutor-card-context">{context}</div>}
                <TutorBiography name={tutor.displayName} biography={tutor.biography} />
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
